import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useColorModeValue } from "@chakra-ui/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import type { PineParseSessionState } from "@/components/Strategies/StrategyPineEditor";
import { StrategyPineEditor } from "@/components/Strategies/StrategyPineEditor";
import { StrategyPineHelpPanel } from "@/components/Strategies/StrategyPineHelpPanel";
import { TesseraFriendlyStructurePanel } from "@/components/Strategies/TesseraFriendlyStructurePanel";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { getReportStrategyDetail, updateReportStrategy } from "../../../services/reportsApi";
import type { ReportStrategyResponse } from "../../../types/reports";
import { canSavePineAfterParse } from "../../../utils/pineIrUi";
import { toastError, presentMutationError } from "../../../utils/showApiErrorToast";
import {
  STRATEGY_DESCRIPTION_SOFT_LIMIT,
  STRATEGY_NAME_SOFT_LIMIT,
} from "../Reports/generateReportConstants";
import { extractPineScriptText } from "../../../utils/reportStrategyPineUi";
import { mergeReportStrategyWithListRow } from "../../../utils/reportStrategyNormalize";
import { BrokerAccountsMultiSelect } from "@/components/Broker/BrokerAccountsMultiSelect";
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount";
import type { BrokerAccountRef } from "@/utils/brokerAccountRef";
import { resolveEntityBrokerAccounts } from "@/utils/brokerAccountRef";
import { filterConnectedTradeAccountsForUi } from "@/utils/tradeAccountSource";

export type EditStrategyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  strategyId: string | undefined;
  listRow?: ReportStrategyResponse | null;
  onSaved: (updated: ReportStrategyResponse) => void | Promise<void>;
  onParseSessionChange?: (session: PineParseSessionState | null) => void;
};

export function EditStrategyDialog({
  open,
  onOpenChange,
  strategyId,
  listRow,
  onSaved,
  onParseSessionChange,
}: EditStrategyDialogProps): React.ReactElement {
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { accounts: tradeAccounts } = useActiveBrokerAccount();
  const accountOptions = useMemo<BrokerAccountRef[]>(() => {
    return filterConnectedTradeAccountsForUi(tradeAccounts)
      .map((account) => ({
        brokerName: String(account.brokerName || "").trim().toLowerCase(),
        brokerAccountCode: String(account.accountCode || "").trim(),
      }))
      .filter((ref) => ref.brokerName && ref.brokerAccountCode);
  }, [tradeAccounts]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pinescript, setPinescript] = useState("");
  const [originalPinescript, setOriginalPinescript] = useState("");
  const [pineSession, setPineSession] = useState<PineParseSessionState | null>(null);
  const [strategyFields, setStrategyFields] = useState<ReportStrategyResponse | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<BrokerAccountRef[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
    setPinescript("");
    setOriginalPinescript("");
    setPineSession(null);
    setStrategyFields(null);
    setSelectedAccounts([]);
    setLoading(false);
    setSaving(false);
    onParseSessionChange?.(null);
  }, [onParseSessionChange]);

  const applyStrategyFields = useCallback(
    (merged: ReportStrategyResponse) => {
      setStrategyFields(merged);
      setName(merged.name ?? "");
      setDescription(merged.description ?? "");
      const pine = extractPineScriptText(merged);
      setPinescript(pine);
      setOriginalPinescript(pine);
      setPineSession(null);
      onParseSessionChange?.(null);
      setSelectedAccounts(
        resolveEntityBrokerAccounts({
          accounts: merged.accounts,
          brokerName: merged.broker_name,
          brokerAccountCode: merged.broker_account_code,
        })
      );
    },
    [onParseSessionChange]
  );

  useEffect(() => {
    if (!open || !strategyId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const detail = await getReportStrategyDetail(strategyId);
        if (cancelled) return;
        applyStrategyFields(mergeReportStrategyWithListRow(detail, listRow));
      } catch (error) {
        if (cancelled) return;
        presentMutationError(error, "Could not load strategy");
        if (listRow) {
          applyStrategyFields(mergeReportStrategyWithListRow(listRow, listRow));
        } else {
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [applyStrategyFields, listRow, onOpenChange, open, strategyId]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetForm();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetForm]
  );

  const handleParseSessionChange = useCallback(
    (session: PineParseSessionState | null) => {
      setPineSession(session);
      onParseSessionChange?.(session);
    },
    [onParseSessionChange]
  );

  const pineChanged = useMemo(
    () => pinescript.trim() !== originalPinescript.trim(),
    [originalPinescript, pinescript]
  );

  const pineReady = useMemo(() => {
    if (!pineChanged) return true;
    const trimmed = pinescript.trim();
    if (!trimmed) return false;
    return canSavePineAfterParse(pinescript, pineSession?.parsedScript ?? null);
  }, [pineChanged, pineSession?.parsedScript, pinescript]);

  const canSave = useMemo(() => {
    if (!name.trim() || !pineReady || selectedAccounts.length === 0) return false;
    return true;
  }, [name, pineReady, selectedAccounts.length]);

  const handleSave = useCallback(async () => {
    if (!strategyId || !strategyFields) return;

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedPine = pinescript.trim();
    const pineBaseline = originalPinescript.trim();
    const accounts = selectedAccounts.map((account) => ({
      brokerName: account.brokerName.trim().toLowerCase(),
      brokerAccountCode: account.brokerAccountCode.trim(),
    }));

    if (!trimmedName) {
      toast.warning("Name required", { description: "Enter a strategy name." });
      return;
    }
    if (accounts.length === 0) {
      toast.warning("Account required", {
        description: "Select at least one broker account for this strategy.",
      });
      return;
    }
    if (trimmedPine !== pineBaseline) {
      if (!trimmedPine) {
        toast.warning("Pinescript required", {
          description: "Clearing Pinescript is not supported. Paste an updated script or leave it unchanged.",
        });
        return;
      }
      if (!canSavePineAfterParse(pinescript, pineSession?.parsedScript ?? null)) {
        toast.warning("Parse required", {
          description: "Click Parse after changing your Pinescript before saving.",
        });
        return;
      }
    }

    const primary = accounts[0]!;
    setSaving(true);
    try {
      const updated = await updateReportStrategy(strategyId, {
        name: trimmedName.slice(0, STRATEGY_NAME_SOFT_LIMIT),
        description: trimmedDescription.slice(0, STRATEGY_DESCRIPTION_SOFT_LIMIT),
        accounts,
        broker_account_code: primary.brokerAccountCode,
        broker_name: primary.brokerName,
        ...(trimmedPine !== pineBaseline && trimmedPine ? { pinescript: trimmedPine } : {}),
      });
      toast.success("Strategy updated", {
        description: updated.compose_invalidated
          ? "Master Pinescript updated. Your active compose is outdated. Re-run TradingView compose when ready."
          : undefined,
      });
      resetForm();
      onOpenChange(false);
      await onSaved(updated);
    } catch (error) {
      presentMutationError(error, "Update failed");
    } finally {
      setSaving(false);
    }
  }, [
    description,
    name,
    onOpenChange,
    onSaved,
    originalPinescript,
    pineSession?.parsedScript,
    pinescript,
    resetForm,
    selectedAccounts,
    strategyFields,
    strategyId,
  ]);

  const showParseHint = !pineReady && pineChanged && Boolean(pinescript.trim());

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Edit strategy"
      description="Update your strategy name, description, and master Pinescript source."
      size="wide"
      contentProps={{ "data-edit-strategy-dialog": "" }}
      contentClassName="sm:max-w-[900px]"
      dismissLabel="Cancel"
      footer={
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="shrink-0"
          disabled={!canSave || loading || saving}
          onClick={() => void handleSave()}
        >
          {saving ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10">
          <Loader2 className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
          <span className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white">
            Loading strategy from server…
          </span>
        </div>
      ) : (
        <FieldGroup className="gap-4" data-edit-strategy-dialog-fields="">
          <Field>
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Accounts</FieldLabel>
            <BrokerAccountsMultiSelect
              options={accountOptions}
              selected={selectedAccounts}
              onChange={setSelectedAccounts}
              emptyLabel="Choose accounts…"
              disabled={loading || saving}
            />
            <FieldDescription className="font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white">
              Strategies can belong to one or more broker accounts.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="edit-strategy-name" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Name
            </FieldLabel>
            <Input
              id="edit-strategy-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={STRATEGY_NAME_SOFT_LIMIT}
              placeholder="Strategy name"
              className="font-[family-name:var(--font-primary)]"
            />
            <FieldDescription className="font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white">
              {name.length}/{STRATEGY_NAME_SOFT_LIMIT}
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="edit-strategy-description" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Description (optional)
            </FieldLabel>
            <Textarea
              id="edit-strategy-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={STRATEGY_DESCRIPTION_SOFT_LIMIT}
              placeholder="Describe your strategy setup, rules, and constraints…"
              className="min-h-[100px] font-[family-name:var(--font-primary)]"
            />
            <FieldDescription className="font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white">
              {description.length}/{STRATEGY_DESCRIPTION_SOFT_LIMIT}
            </FieldDescription>
          </Field>

          <TesseraFriendlyStructurePanel />

          <StrategyPineEditor
            key={strategyId}
            value={pinescript}
            onChange={setPinescript}
            subtleText={subtleText}
            textColor={textColor}
            borderColor={borderColor}
            minH="300px"
            brandFields
            textareaId="edit-strategy-pinescript"
            savedFields={strategyFields ?? listRow ?? undefined}
            onParseSessionChange={handleParseSessionChange}
          />

          <StrategyPineHelpPanel />

          {showParseHint ? (
            <p className="font-[family-name:var(--font-primary)] text-xs font-normal text-gray-500 dark:text-white">
              Parse your Pinescript after edits before saving.
            </p>
          ) : null}
        </FieldGroup>
      )}
    </StrategyPineDetailDialog>
  );
}
